import React, { useState, useEffect, useMemo, useCallback } from "react";
import { format, addDays, startOfWeek, isBefore, startOfDay, isAfter } from "date-fns";
import axios from "axios";
import {
  TextField,
  Select,
  MenuItem,
  Button,
  Box,
  FormControl,
  InputLabel,
} from "@mui/material";

const getColorForTask = (taskName) => {
  const colors = [
    "#3B82F6", "#F59E0B", "#10B981", "#8B5CF6",
    "#EC4899", "#F87171", "#0EA5E9", "#22C55E",
    "#EAB308", "#6366F1", "#EF4444", "#14B8A6"
  ];
  let hash = 0;
  for (let i = 0; i < taskName.length; i++) {
    hash = taskName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const formatDecimalHoursToTime = (hoursFloat) => {
  const totalMinutes = Math.round(hoursFloat * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

const GanttView = ( {projectId} ) => {
  const [weekStartDate, setWeekStartDate] = useState(() => {
      const today = new Date();
      return today.toISOString().split('T')[0]; // "YYYY-MM-DD"
    });  
  const [tasks, setTasks] = useState([]);
  const [draganddroptasks, setdraganddropTasks] = useState([]);
  const [formOpenDate, setFormOpenDate] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [userList, setUserList] = useState([]);
  const [newTask, setNewTask] = useState({
    task_name: "",
    assign_to: "",
    submission_date: "",
    priority: "Medium"
  });

 
  useEffect(() => {
    axios.get("/api/users.php")
      .then(res => setUserList(res.data))
      .catch(err => console.error("Error fetching users", err));
  }, []);

  const weekStart = startOfWeek(new Date(weekStartDate), { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekDates = days.map((d) => format(d, "yyyy-MM-dd"));

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const res = await fetch(`/api/getTasksByUser.php?user=${selectedUser}`);
        const data = await res.json();
        setTasks(data);
      } catch (err) {
        console.error("❌ Error fetching tasks:", err);
      }
    };

    if (selectedUser) fetchTasks();
  }, [selectedUser]);

const handleDrop = useCallback(async (e, newDate) => {
  if (!selectedUser) {
    alert("Select a user to assign the task.");
    return;
  }

  const today = format(new Date(), "yyyy-MM-dd");
  if (isBefore(new Date(newDate), new Date(today)) || newDate === today) return;

  const taskId = e.dataTransfer.getData("task_id");

  try {
    await axios.post("/api/updateTaskStartDate.php", {
      id: taskId,
      start_date: newDate,
      assign_to: selectedUser,
    });

    // Refresh assigned tasks
    const res = await fetch(`/api/getTasksByUser.php?user=${selectedUser}`);
    const data = await res.json();
    setTasks(data);

    // Refresh unassigned tasks
    const unassigned = await fetch(`/api/getTasksByProjectfordrapanddrop.php?project_id=${projectId}`);
    const unassignedData = await unassigned.json();
    setdraganddropTasks(unassignedData);
  } catch (error) {
    console.error("Error updating task:", error);
  }
}, [selectedUser, projectId]);


  const handleDragStart = (e, taskId, date) => {
    const today = format(new Date(), "yyyy-MM-dd");
    if (isBefore(new Date(date), new Date(today)) || date === today) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData("task_id", taskId);
  };

  const handleSaveTask = async (date) => {
    try {
      await axios.post('/api/projecttask.php', {
        task_name: newTask.task_name,
        assign_to: selectedUser,
        submission_date: newTask.submission_date,
        priority: newTask.priority,
        start_date: date,
        status: 'To Do',
        project_id: '',
      });
      setFormOpenDate(null);
      setNewTask({ task_name: '', assign_to: '', submission_date: '', priority: 'Medium' });
      const res = await fetch(`/api/getTasksByUser.php?user=${selectedUser}`);
      const data = await res.json();
      setTasks(data);
    } catch (error) {
      console.error("Error saving task:", error);
    }
  };

  const dayTaskMap = useMemo(() => {
    const map = {};

    const clampSessionToWorkingHours = (start, end) => {
      const WORK_INTERVALS = [
        { from: { h: 9, m: 30 }, to: { h: 13, m: 30 } },
        { from: { h: 14, m: 0 }, to: { h: 18, m: 0 } },
      ];
      let totalMs = 0;
      let current = new Date(start);

      const getIntervalDuration = (day, sessionStart, sessionEnd) => {
        if (day === 0) return 0;
        return WORK_INTERVALS.reduce((sum, { from, to }) => {
          const intervalStart = new Date(sessionStart);
          intervalStart.setHours(from.h, from.m, 0, 0);
          const intervalEnd = new Date(sessionEnd);
          intervalEnd.setHours(to.h, to.m, 0, 0);
          const fromTime = new Date(Math.max(sessionStart.getTime(), intervalStart.getTime()));
          const toTime = new Date(Math.min(sessionEnd.getTime(), intervalEnd.getTime()));
          return fromTime < toTime ? sum + (toTime - fromTime) : sum;
        }, 0);
      };

      while (current < end) {
        const day = current.getDay();
        const nextMidnight = new Date(current);
        nextMidnight.setHours(23, 59, 59, 999);
        const sessionEnd = new Date(Math.min(nextMidnight.getTime(), end.getTime()));
        totalMs += getIntervalDuration(day, current, sessionEnd);
        current.setDate(current.getDate() + 1);
        current.setHours(0, 0, 0, 0);
      }
      return totalMs;
    };

    tasks.forEach((task) => {
      let sessions = [];
      try {
        sessions = JSON.parse(task.duration || "[]");
      } catch {
        sessions = [];
      }

      const taskStartDate = task.start_date;

      sessions.forEach((session) => {
        if (!session.start) return;
        const start = new Date(session.start);
        const end = session.end
          ? new Date(session.end)
          : session.pause
          ? new Date(session.pause)
          : new Date();

        const sessionDate = start.toISOString().split("T")[0];
        const durationMs = clampSessionToWorkingHours(start, end);
        const hours = parseFloat((durationMs / 3600000).toFixed(2));
        if (!map[sessionDate]) map[sessionDate] = {};
        const key = task.task_name;
        if (!map[sessionDate][key]) {
          map[sessionDate][key] = {
            id: task.id,
            taskName: task.task_name,
            hours: 0,
            color: getColorForTask(task.task_name),
          };
        }
        map[sessionDate][key].hours += hours;
      });

      if (taskStartDate && (!sessions || sessions.length === 0)) {
        if (!map[taskStartDate]) map[taskStartDate] = {};
        const key = task.task_name;
        const scheduledHours = parseFloat(task.submission_date || 0);
        if (!map[taskStartDate][key]) {
          map[taskStartDate][key] = {
            id: task.id,
            taskName: task.task_name,
            hours: scheduledHours,
            color: getColorForTask(task.task_name),
            scheduledOnly: true,
          };
        } else {
          map[taskStartDate][key].hours += scheduledHours;
        }
      }
    });

    const result = {};
    for (const date in map) {
      result[date] = Object.values(map[date]).map((t) => ({
        ...t,
        duration: formatDecimalHoursToTime(t.hours),
        hours: parseFloat(t.hours.toFixed(2)),
      }));
    }

    return result;
  }, [tasks]);

  useEffect(() => {
  const fetchTasksByProject = async () => {
    try {
      const res = await fetch(`/api/getTasksByProjectfordrapanddrop.php?project_id=${projectId}`);
      const data = await res.json();
      setdraganddropTasks(data);
    } catch (err) {
      console.error("❌ Error fetching project tasks:", err);
    }
  };

  if (projectId) fetchTasksByProject();
}, [projectId]);

const unassignedTasks = useMemo(() => {
  return draganddroptasks.filter(
    (t) =>
      t.project_id === projectId &&
      (!t.assign_to || t.assign_to === "NULL" || t.assign_to === null || t.assign_to === "")
  );
}, [draganddroptasks, projectId]);



  return (
    <div className="space-y-4 mt-2 flex ">
    {unassignedTasks.length > 0 && (
      <div className=" p-2 bg-red-50 border border-red-300 rounded mr-3">
        <div className="text-xs font-semibold text-red-700 mb-2">Unassigned Tasks</div>
          <div className=" gap-2 overflow-x-auto">
            {unassignedTasks.map((t) => (
              <div
                key={t.id}
                className="text-white text-xs rounded-md px-3 py-1 shadow text-center cursor-move mb-3"
                style={{ backgroundColor: getColorForTask(t.task_name), minWidth: "120px" }}
                draggable={!!selectedUser}
                onDragStart={(e) => {
                  if (!selectedUser) {
                    e.preventDefault();
                    alert("Please select a user before assigning tasks.");
                    return;
                  }
                  e.dataTransfer.setData("task_id", t.id);
                }}
              >
                {t.task_name}: {formatDecimalHoursToTime(parseFloat(t.submission_date || 0))}h
              </div>
            ))}
          </div>
        </div>
      )}
       <div className="bg-white p-10 pb-5 w-7xl rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xl font-semibold text-gray-700">Time Sheet</div>
          <div className="flex items-center gap-4">
            <div className="flex flex-col text-xs text-gray-600">
              <label>Select User:</label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="px-2 py-1 rounded-md text-sm border border-gray-300 shadow-sm focus:ring focus:ring-blue-200"
              >
                <option>Select User</option>
                {userList.map((u) => (
                  <option key={u.id} value={u.name}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col text-xs text-gray-600">
              <label>Select Week:</label>
              <input
                type="date"
                value={format(weekStart, "yyyy-MM-dd")}
                onChange={(e) => setWeekStartDate(e.target.value)}
                className="px-2 py-1 rounded-md text-sm border border-gray-300 shadow-sm focus:ring focus:ring-blue-200"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg shadow-xl bg-white">
          <div className="grid grid-cols-7 text-center border border-gray-200 bg-gray-700 text-sm font-medium">
            {days.map((day, idx) => (
              <div key={idx} className="px-4 py-2 border-r border-gray-200 text-white">
                <div className="text-xs uppercase">{format(day, "EEE")}</div>
                <div className="text-[11px] font-semibold">{format(day, "MMM d")}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 text-sm min-h-[380px]">
            {weekDates.map((date, idx) => {
              const dayTasks = dayTaskMap[date] || [];
              const totalHours = dayTasks.reduce((sum, t) => sum + t.hours, 0);
              const remaining = parseFloat((8 - totalHours).toFixed(2));
              const isFuture = new Date(date) > new Date();

              return (
                <div
                key={idx}
                className="border-r border-gray-300 p-4 min-h-[100px] bg-gray-50 hover:bg-gray-100"
                onDragOver={(e) => isFuture && e.preventDefault()}
                onDrop={(e) => isFuture && handleDrop(e, date)}
                >
                {dayTasks.map((t, i) => (
                  <div
                    key={i}
                    className="text-white text-xs rounded-md px-2 py-2 mb-2 shadow-md text-center"
                    style={{ backgroundColor: t.color }}
                    draggable={isAfter(new Date(date), new Date())}
                    onDragStart={(e) => handleDragStart(e, t.id, date)}
                  >
                    {t.taskName}: {formatDecimalHoursToTime(t.hours)}h
                  </div>
                ))}

                {remaining > 0 && !isBefore(startOfDay(new Date(date)), startOfDay(new Date())) && (
                  <>
                    <div
                      className="group relative w-full text-xs rounded-md px-2 py-1 mb-1 bg-gray-200 border border-dotted border-gray-400 text-center h-6 flex items-center justify-center cursor-pointer"
                      onClick={() => {
                        setFormOpenDate(date);
                        setNewTask({
                          task_name: "",
                          assign_to: "",
                          submission_date: "",
                          priority: "Medium"
                        });
                      }}
                    >
                      <span className="group-hover:hidden text-gray-700">
                        Free time: {formatDecimalHoursToTime(remaining)}h
                      </span>
                      <span className="hidden group-hover:inline text-gray-700 font-semibold text-xs absolute border border-dotted border-gray-900 rounded-full px-1">
                        +
                      </span>
                    </div>

                    {formOpenDate === date && (
                      <Box className="bg-white shadow border border-gray-300 rounded-md text-xs" sx={{ p: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <TextField
                          size="small"
                          label="Task Name"
                          variant="outlined"
                          value={newTask.task_name}
                          onChange={(e) => setNewTask({ ...newTask, task_name: e.target.value })}
                          fullWidth
                          sx={{ '& .MuiInputBase-input': { fontSize: '12px' }, '& .MuiInputLabel-root': { fontSize: '12px' } }}
                        />
                        <TextField
                          size="small"
                          label="Tentative Time"
                          type="number"
                          value={newTask.submission_date}
                          onChange={(e) => setNewTask({ ...newTask, submission_date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          sx={{ '& .MuiInputBase-input': { fontSize: '12px' }, '& .MuiInputLabel-root': { fontSize: '12px' } }}
                        />
                        <FormControl fullWidth size="small" sx={{
                          '& .MuiInputBase-root': { fontSize: '12px' },
                          '& .MuiInputLabel-root': { fontSize: '12px' },
                          '& .MuiMenuItem-root': { fontSize: '12px' }
                        }}>
                          <InputLabel>Priority</InputLabel>
                          <Select
                            value={newTask.priority}
                            label="Priority"
                            onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                          >
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="Urgent">Urgent</MenuItem>
                          </Select>
                        </FormControl>
                        <Box display="flex" gap={1} pt={0}>
                          <Button variant="contained" color="primary" sx={{ py: 0.5, fontSize: '9px', px: 0 }} onClick={() => handleSaveTask(date)}>
                            Save
                          </Button>
                          <Button variant="outlined" color="secondary" sx={{ py: 0.5, fontSize: '9px', px: 0 }} onClick={() => setFormOpenDate(null)}>
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
};

export default GanttView;